import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Plus } from "react-feather";
import { typographyMixin } from "../design-system/tokens/typography";
import TooltipPopup from "./TooltipPopup";
import { useAssistantPanel } from "../contexts/AssistantPanelContext";
import { useGlobalPath } from "../hooks/useGlobalPath";
import { useStoryWorkspace } from "../context/StoryWorkspaceContext";
import { AccountMenuChip } from "./organisms/AccountMenuChip";
import { ContextNavigationRail } from "./organisms/ContextNavigationRail";
import { PrimaryNavDrawer } from "./organisms/PrimaryNavDrawer";
import { SwitchTenantModal } from "./organisms/SwitchTenantModal";
import { getActiveTenantId } from "../services/workspace/activeWorkspace";
import { useProfileMenuActions, SWITCH_TENANT_EVENT } from "../hooks/useProfileMenuActions";
import { useCreateStoryCommand } from "../hooks/useAppCommands";
import { CreateDataStoryFlowModal } from "./organisms/CreateDataStoryFlowModal";
import { ModelStatusBanner } from "./organisms/ModelStatusBanner";
import { useStoryCreationPath } from "../hooks/useArchitecture";
import type { CreateStoryRequest } from "../services/dataStories/storiesApi.types";
import { SidebarPromoCard } from "./molecules/SidebarPromoCard";
import {
  buildSidebarMainItems,
  SIDEBAR_SIMPLIFIED_EXTRA_ITEM,
} from "./Sidebar.config";
import type { PrimaryNavigationMode } from "./layout/layoutRegistry";
import { resolvePrimaryNavigationMode } from "./layout/primaryNavigationLayout";
import { rawColors } from "../design-system/tokens/colors";

const LogoItem = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    background-color: #e6f2ff;
  }

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
`;

const SidebarContainer = styled.aside`
  grid-area: sidebar;
  /* Fill the Shell sidebar slot's full height (= viewport minus header)
     so the inner SidebarContainerSimplified can flex to 100% — matches
     the main panel's vertical extent exactly. */
  height: 100%;
  box-sizing: border-box;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const IconGroup = styled.div`
  background-color: ${rawColors.structural.white};
  border-radius: 8px;
  padding: 3px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const TopSection = styled.div`
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: auto;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #1e90ff;
  color: ${rawColors.text.white};
  border: none;
  border-radius: 4px;
  padding: 10px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #1a7ae0;
  }
`;

const SidebarContainerSimplified = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  /* Fill the SidebarContainer's flex column instead of a magic
     calc(100vh - 88px) — keeps the sidebar bottom aligned with the
     KgMainPanel bottom regardless of the header's actual height. */
  flex: 1 1 auto;
  min-height: 0;
  /* BottomIconRow bleeds to the panel edge with negative margins; clip it so no horizontal scrollbar appears. */
  min-width: 0;
  overflow-x: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border.stroke};
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.background.primary};
  padding: 10px;
  box-sizing: border-box;
  width: 270px;
`;

const SidebarItemSimplified = styled(Link)<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  ${typographyMixin("body", 4)}
  color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.text.interactive : theme.colors.text.primary};
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.primary[50] : "transparent"};
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary[50]};
    color: ${({ theme }) => theme.colors.text.interactive};
  }

  /* Figma splits this into two tokens: label = text/high-emphasis (set via
     the Link's own color, above), icon = text/medium-emphasis. Overriding
     currentColor on the svg itself keeps that distinction without touching
     each icon usage individually. */
  svg {
    flex-shrink: 0;
    color: ${({ theme, $isSelected }) =>
      $isSelected ? theme.colors.text.interactive : theme.colors.text.secondary};
  }

  &:hover svg {
    color: ${({ theme }) => theme.colors.text.interactive};
  }
`;

const CreateButtonSimplified = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  gap: 8px;
  background-color: ${({ theme }) => theme.colors.background.inverse};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: 8px;
  padding: 8px 16px 8px 12px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.inverseHover};
  }

  svg {
    flex-shrink: 0;
  }
`;

/** No react-feather equivalent for a browser-extension/plugin glyph — inline SVG. */
const ExtensionIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M7.39783 17.0833H4.16699C3.82088 17.0833 3.52602 16.9615 3.28241 16.7179C3.0388 16.4743 2.91699 16.1794 2.91699 15.8333V12.6025C3.48755 12.5331 3.97741 12.2959 4.38658 11.891C4.79574 11.486 5.00033 10.9946 5.00033 10.4167C5.00033 9.83875 4.79574 9.34729 4.38658 8.94229C3.97741 8.53743 3.48755 8.30028 2.91699 8.23084V5C2.91699 4.65389 3.0388 4.35903 3.28241 4.11542C3.52602 3.87181 3.82088 3.75 4.16699 3.75H7.50033C7.57519 3.22014 7.80727 2.7832 8.19658 2.43917C8.58602 2.09514 9.04838 1.92313 9.58366 1.92313C10.1189 1.92313 10.5813 2.09514 10.9707 2.43917C11.36 2.7832 11.5921 3.22014 11.667 3.75H15.0003C15.3464 3.75 15.6413 3.87181 15.8849 4.11542C16.1285 4.35903 16.2503 4.65389 16.2503 5V8.33334C16.7802 8.4082 17.2171 8.64028 17.5612 9.02958C17.9052 9.41903 18.0772 9.88139 18.0772 10.4167C18.0772 10.9519 17.9052 11.4143 17.5612 11.8038C17.2171 12.1931 16.7802 12.4251 16.2503 12.5V15.8333C16.2503 16.1794 16.1285 16.4743 15.8849 16.7179C15.6413 16.9615 15.3464 17.0833 15.0003 17.0833H11.7695C11.6948 16.485 11.4507 15.9882 11.0372 15.5929C10.6237 15.1976 10.1392 15 9.58366 15C9.0281 15 8.54359 15.1976 8.13012 15.5929C7.71665 15.9882 7.47255 16.485 7.39783 17.0833ZM4.16699 15.8333H6.42658C6.71185 15.0983 7.16248 14.5674 7.77845 14.2404C8.39428 13.9135 8.99602 13.75 9.58366 13.75C10.1713 13.75 10.773 13.9135 11.3889 14.2404C12.0048 14.5674 12.4555 15.0983 12.7407 15.8333H15.0003V11.2981H16.026C16.2812 11.266 16.4786 11.1629 16.618 10.9888C16.7575 10.8146 16.8272 10.6239 16.8272 10.4167C16.8272 10.2094 16.7575 10.0188 16.618 9.84459C16.4786 9.67042 16.2812 9.56729 16.026 9.53521H15.0003V5H10.4651V3.97438C10.433 3.7191 10.3299 3.52174 10.1557 3.38229C9.98158 3.24285 9.79088 3.17313 9.58366 3.17313C9.37644 3.17313 9.18574 3.24285 9.01158 3.38229C8.83741 3.52174 8.73428 3.7191 8.7022 3.97438V5H4.16699V7.28208C4.79949 7.54389 5.30484 7.95972 5.68303 8.52958C6.06123 9.09959 6.25033 9.72861 6.25033 10.4167C6.25033 11.0961 6.06123 11.7208 5.68303 12.2908C5.30484 12.8608 4.79949 13.281 4.16699 13.5513V15.8333Z"
      fill="currentColor"
    />
  </svg>
);

/** Shared 40×40 square-button recipe for the bottom icon row (extension + settings). */
const bottomIconButtonRules = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primary[50]};
  }

  svg {
    width: 20px;
    height: 20px;
    aspect-ratio: 1 / 1;
  }
`;

const BottomIconButton = styled.button`
  ${bottomIconButtonRules}
`;

/**
 * Full-bleed footer strip: SidebarContainerSimplified has 10px padding on
 * all sides, so a plain `width: 100%` border-top stops short of the card's
 * edges. Negative margin pulls this row out to the padding box's edge (the
 * inner face of the card's 1px border) and the bottom radius is reduced by
 * the border width (12px - 1px) so the fill doesn't square off past the
 * card's own rounded corner.
 */
const BottomIconRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  margin: 0 -10px -10px;
  padding: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.stroke};
  background-color: ${({ theme }) => theme.colors.background.primary};
  border-radius: 0 0 11px 11px;
`;

interface SidebarProps {
  toggleAnnotationsList: () => void;
  /** Optional registry-owned presentation; omitted routes retain legacy behavior. */
  primaryNavigation?: PrimaryNavigationMode;
  /** Drawer mode: whether the overlay drawer is open. */
  drawerOpen?: boolean;
  onDrawerClose?: () => void;
  /** Drawer mode: hover preview + pin (wide) or tap overlay (narrow). */
  drawerVariant?: "modal" | "peek";
  drawerDocked?: boolean;
  onDrawerPinToggle?: () => void;
  onDrawerPointerEnter?: () => void;
  onDrawerPointerLeave?: () => void;
}

const noop = () => undefined;

const Sidebar: React.FC<SidebarProps> = ({
  toggleAnnotationsList,
  primaryNavigation,
  drawerOpen = false,
  onDrawerClose,
  drawerVariant,
  drawerDocked,
  onDrawerPinToggle,
  onDrawerPointerEnter,
  onDrawerPointerLeave,
}) => {
  const [isTargetPopupOpen, setIsTargetPopupOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState(0);
  const [isAnnotationModeActive, setIsAnnotationModeActive] = useState(false);
  const [toast, setToast] = useState(null);
  const [isPromoCardOpen, setIsPromoCardOpen] = useState(true);
  const [isCreateStoryModalOpen, setIsCreateStoryModalOpen] = useState(false);
  // The method highlighted when the flow opens: the empty state's "Blank story" opens it on the blank canvas.
  const [createStoryRecipe, setCreateStoryRecipe] = useState<"blank-canvas" | undefined>(undefined);
  const [isCreatingStory, setIsCreatingStory] = useState(false);
  const [isSwitchTenantOpen, setIsSwitchTenantOpen] = useState(false);
  const targetIconRef = useRef<HTMLAnchorElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const {
    isGlobalPath,
    isLoginPath,
    isConnectorDetailPath,
    isKnowledgeGraphPath,
    isStoryDetailPath,
  } = useGlobalPath();
  const { createStory } = useStoryWorkspace();
  const storyPath = useStoryCreationPath();
  const navigate = useNavigate();
  const resolvedNavigationMode = resolvePrimaryNavigationMode(
    primaryNavigation,
    {
      isGlobalPath,
      isConnectorDetailPath,
      isKnowledgeGraphPath,
    },
  );

  const sidebarMainItems = React.useMemo(
    () =>
      buildSidebarMainItems(),
    [],
  );
  const contextNavigationItems = React.useMemo(
    () => sidebarMainItems.filter((item) => item.showInContextRail),
    [sidebarMainItems],
  );


  const profileMenu = useProfileMenuActions();
  const handleSwitchTenantClose = React.useCallback(
    () => setIsSwitchTenantOpen(false),
    [],
  );
  useEffect(() => {
    const open = () => setIsSwitchTenantOpen(true);
    window.addEventListener(SWITCH_TENANT_EVENT, open);
    return () => window.removeEventListener(SWITCH_TENANT_EVENT, open);
  }, []);

  const { error: storyPathError, retry: retryStoryPath } = storyPath;
  const handleCreateStoryClick = React.useCallback(() => {
    if (storyPathError) retryStoryPath();
    setCreateStoryRecipe(undefined);
    setIsCreateStoryModalOpen(true);
  }, [storyPathError, retryStoryPath]);
  const handleCreateBlankStoryClick = React.useCallback(() => {
    if (storyPathError) retryStoryPath();
    setCreateStoryRecipe("blank-canvas");
    setIsCreateStoryModalOpen(true);
  }, [storyPathError, retryStoryPath]);
  // Command palette, chat and voice: "New story" opens the same flow.
  useCreateStoryCommand(handleCreateStoryClick, handleCreateBlankStoryClick);

  const handleCreateStorySubmit = React.useCallback(
    async (request: CreateStoryRequest) => {
      setIsCreatingStory(true);
      try {
        await createStory(navigate, setToast, request, storyPath.version);
        setIsCreateStoryModalOpen(false);
      } finally {
        setIsCreatingStory(false);
      }
    },
    [createStory, navigate, storyPath.version],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isTargetPopupOpen &&
        targetIconRef.current &&
        tooltipRef.current &&
        !targetIconRef.current.contains(event.target as Node) &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsTargetPopupOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isTargetPopupOpen]);

  const handleTargetClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const targetRect = e.currentTarget.getBoundingClientRect();
    const tooltipTop = targetRect.top + targetRect.height / 2 - 30;
    setPopupPosition(tooltipTop);
    setIsTargetPopupOpen(!isTargetPopupOpen);
    toggleAnnotationsList();
  };

  // The MagicBI icon opens the contextual MagicBI panel.
  const assistantPanel = useAssistantPanel();
  const toggleAnnotationMode = () => {
    setIsAnnotationModeActive(!isAnnotationModeActive);
  };

  if (isLoginPath || resolvedNavigationMode === "hidden") {
    return null;
  }

  return (
    <>
      {resolvedNavigationMode === "drawer" && (
        <PrimaryNavDrawer
          open={drawerOpen}
          onClose={onDrawerClose ?? noop}
          items={[...sidebarMainItems, SIDEBAR_SIMPLIFIED_EXTRA_ITEM]}
          pathname={location.pathname}
          onCreate={handleCreateStoryClick}
          onInvite={() => navigate("/workspaces")}
          onConnect={() => navigate("/data-connectors")}
          variant={drawerVariant}
          docked={drawerDocked}
          onPinToggle={onDrawerPinToggle}
          onPointerEnter={onDrawerPointerEnter}
          onPointerLeave={onDrawerPointerLeave}
        />
      )}
      {resolvedNavigationMode !== "drawer" && (
      <SidebarContainer
        data-primary-navigation-mode={resolvedNavigationMode}
        data-testid="primary-navigation"
      >
        {resolvedNavigationMode === "wide" && (
          <SidebarContainerSimplified>
            <TopSection>
              {[...sidebarMainItems, SIDEBAR_SIMPLIFIED_EXTRA_ITEM].map(
                ({ id, to, label, icon: Icon, isSelected, soon }) =>
                  soon ? (
                    <SidebarItemSimplified
                      key={id}
                      as="span"
                      // Rendered as a span: no link target. `to` is required by the Link type only.
                      to={undefined as never}
                      aria-disabled="true"
                      aria-label={`${label}, coming soon`}
                      title="Coming soon"
                      style={{ cursor: "not-allowed", opacity: 0.5 }}
                    >
                      <Icon size={16} />
                      <span>{label}</span>
                    </SidebarItemSimplified>
                  ) : (
                  <SidebarItemSimplified
                    key={id}
                    to={to}
                    $isSelected={isSelected(location.pathname)}
                  >
                    <Icon size={16} />
                    <span>{label}</span>
                  </SidebarItemSimplified>
                  ),
              )}
            </TopSection>
            <BottomSection>
              {isPromoCardOpen && (
                <SidebarPromoCard onDismiss={() => setIsPromoCardOpen(false)} />
              )}
              <CreateButtonSimplified
                onClick={handleCreateStoryClick}
              >
                <Plus size={16} />
                <span>Create data story</span>
              </CreateButtonSimplified>
              <BottomIconRow>
                <BottomIconButton type="button" aria-label="Browser extension">
                  <ExtensionIcon />
                </BottomIconButton>
                <AccountMenuChip
                  compact
                  placement="right-end"
                />
              </BottomIconRow>
            </BottomSection>
          </SidebarContainerSimplified>
        )}
        {resolvedNavigationMode === "compact" && (
          <ContextNavigationRail
            // Story pages get every module (one click each) in the collapsed, expandable rail.
            items={isStoryDetailPath ? sidebarMainItems : contextNavigationItems}
            isExpandable={isStoryDetailPath}
            utilityItems={[SIDEBAR_SIMPLIFIED_EXTRA_ITEM]}
            pathname={location.pathname}
            profile={
              <AccountMenuChip
                compact
                placement="right-end"
              />
            }
            onCreate={handleCreateStoryClick}
          />
        )}
        {resolvedNavigationMode === "legacy" && isStoryDetailPath && (
          <IconGroup>
            <LogoItem to="/home">
              <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Logo" />
            </LogoItem>
          </IconGroup>
        )}
      </SidebarContainer>
      )}
      {/* Opens once the tenant's path is known, so the old flow never shows for a moment. */}
      <CreateDataStoryFlowModal
        open={isCreateStoryModalOpen && storyPath.isReady}
        onClose={() => setIsCreateStoryModalOpen(false)}
        onSubmit={handleCreateStorySubmit}
        isSubmitting={isCreatingStory}
        newPath={storyPath.newPath}
        initialRecipeId={createStoryRecipe}
        notice={<ModelStatusBanner placement="createStory" />}
      />
      <SwitchTenantModal
        open={isSwitchTenantOpen}
        onClose={handleSwitchTenantClose}
        currentTenantId={getActiveTenantId()}
        onUnauthorized={profileMenu.onLogout}
      />
    </>
  );
};

export default Sidebar;
